/**
 * Beginner / Expert UI level.
 *
 * IN-MEMORY ONLY — the level is a useState, never written to storage, so it
 * resets to 'beginner' on every load. That honors the frame-0016 "nothing is
 * written" promise (a stored UI flag would dent the pitch); an expert re-flips
 * it in one tap per session. Provider wraps the app; components read it via
 * useUiLevel(); LevelToggle renders the top-bar switch.
 */
import { createContext, useContext, useState, type ReactNode } from 'react';

export type UiLevel = 'beginner' | 'expert';

interface LevelCtx {
  level: UiLevel;
  setLevel: (l: UiLevel) => void;
  toggle: () => void;
}

const Ctx = createContext<LevelCtx | null>(null);

export function UiLevelProvider(props: { children: ReactNode }): JSX.Element {
  const [level, setLevel] = useState<UiLevel>('beginner');
  const toggle = (): void => setLevel((l) => (l === 'beginner' ? 'expert' : 'beginner'));
  return <Ctx.Provider value={{ level, setLevel, toggle }}>{props.children}</Ctx.Provider>;
}

export function useUiLevel(): LevelCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useUiLevel used outside <UiLevelProvider>');
  return c;
}

/** Compact segmented switch for the app top bar. */
export function LevelToggle(): JSX.Element {
  const { level, toggle } = useUiLevel();
  return (
    <button
      type="button"
      className="level-toggle"
      role="switch"
      aria-checked={level === 'expert'}
      aria-label={`Currently ${level} mode — switch to ${level === 'beginner' ? 'expert' : 'beginner'}`}
      onClick={toggle}
    >
      <span className={level === 'beginner' ? 'seg on' : 'seg'}>Simple</span>
      <span className={level === 'expert' ? 'seg on' : 'seg'}>Expert</span>
    </button>
  );
}
